/**
 * StudentPerformance – one student's learning, for the student and for staff.
 *
 * The student sees it as "My Performance" on their dashboard; a teacher or
 * school sees the same view on /analytics/students/:id. The figures are the
 * same; only the wording changes, so a student is never shown staff language
 * like "needs attention" about themselves.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  byTime,
  formatDuration,
  formatPercent,
  formatPp,
  groupPerformance,
  inRange,
  isValid,
  lessonTitle,
  previousRange,
  recentTrend,
  scoreTone,
  strengthsAndGaps,
  summarise,
  withinRange,
  type Result,
} from '../../lib/analytics/metrics';
import type { LaunchRow } from '../../services/performanceService';
import { MetricCard, MetricGrid } from './MetricCard';
import { ComparisonChart, TrendChart } from './charts';
import { DataTable, type Column } from './DataTable';
import { DateRangeFilter, makeRange, type RangeValue } from './DateRangeFilter';
import { AnalyticsEmptyState, AnalyticsErrorState, AnalyticsNote, AnalyticsPanel, ChartSkeleton, TableSkeleton } from './AnalyticsPanel';

export function StudentPerformance({
  results,
  launches = [],
  audience,
  passThreshold,
  loading,
  error,
}: {
  results: Result[];
  launches?: LaunchRow[];
  audience: 'self' | 'staff';
  passThreshold: number;
  loading: boolean;
  error?: string | null;
}) {
  const navigate = useNavigate();
  const [range, setRange] = useState<RangeValue>(() => makeRange('all'));
  const self = audience === 'self';

  const inPeriod = useMemo(() => results.filter((r) => inRange(r, range.range)), [results, range]);
  const previous = useMemo(() => {
    const prev = previousRange(range.range);
    return prev ? results.filter((r) => inRange(r, prev)) : null;
  }, [results, range]);

  const summary = useMemo(() => summarise(inPeriod, passThreshold), [inPeriod, passThreshold]);
  const previousSummary = useMemo(() => (previous ? summarise(previous, passThreshold) : null), [previous, passThreshold]);
  const averageDelta =
    summary.average !== null && previousSummary?.average != null ? summary.average - previousSummary.average : null;

  const lessonsCompleted = useMemo(() => new Set(inPeriod.filter(isValid).map((r) => r.lessonKey)).size, [inPeriod]);
  const trend = useMemo(() => recentTrend(inPeriod), [inPeriod]);

  // Learning time is time spent in lessons; where no launch recorded a
  // duration, fall back to time spent on quizzes, and say which.
  const launchSeconds = launches
    .filter((l) => withinRange(l.launchedAt, range.range))
    .reduce((s, l) => s + (l.durationSeconds ?? 0), 0);
  const learningSeconds = launchSeconds > 0 ? launchSeconds : summary.totalTimeSeconds;

  // Subjects when there is more than one; otherwise chapters within the one subject.
  const areaKey = useMemo(() => {
    const subjects = new Set(inPeriod.filter(isValid).map((r) => r.subject).filter(Boolean));
    return subjects.size >= 2 ? 'subject' : 'chapter';
  }, [inPeriod]);
  const keyOf = (r: Result) => (areaKey === 'subject' ? r.subject : r.chapterId || null);
  const labelOf = (r: Result) => (areaKey === 'subject' ? r.subject || 'Other' : r.chapterName || r.chapterId || 'Chapter');

  const areas = useMemo(
    () =>
      groupPerformance(inPeriod.filter(isValid), keyOf, labelOf, { passThreshold })
        .filter((g) => g.summary.average !== null)
        .sort((a, b) => (b.summary.average ?? 0) - (a.summary.average ?? 0)),
    // keyOf/labelOf depend only on areaKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inPeriod, areaKey, passThreshold]
  );
  const best = areaKey === 'subject' && areas.length >= 2 ? areas[0] : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { strengths, gaps } = useMemo(() => strengthsAndGaps(inPeriod, keyOf, labelOf), [inPeriod, areaKey]);

  const trendData = useMemo(
    () =>
      inPeriod
        .filter((r) => isValid(r) && r.completedAt !== null)
        .sort(byTime)
        .map((r) => ({ x: r.completedAt!, value: r.percentage, title: lessonTitle(r), note: `${r.marksObtained} / ${r.maximumMarks} marks` })),
    [inPeriod]
  );

  const columns: Column<Result>[] = [
    {
      key: 'lesson',
      header: 'Lesson',
      sortValue: (r) => lessonTitle(r),
      cell: (r) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{lessonTitle(r)}</p>
          {r.subject && <p className="truncate text-xs text-muted-foreground">{r.subject}</p>}
        </div>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      sortValue: (r) => r.completedAt,
      cell: (r) => (r.completedAt ? new Date(r.completedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'),
    },
    { key: 'score', header: 'Score', align: 'right', sortValue: (r) => r.marksObtained, cell: (r) => `${r.marksObtained} / ${r.maximumMarks}` },
    {
      key: 'percentage',
      header: 'Percentage',
      align: 'right',
      sortValue: (r) => r.percentage,
      cell: (r) => <span className={`font-semibold ${scoreTone(isValid(r) ? r.percentage : null, passThreshold)}`}>{formatPercent(r.percentage)}</span>,
    },
    { key: 'accuracy', header: 'Accuracy', align: 'right', hideOnMobile: true, sortValue: (r) => r.accuracy, cell: (r) => formatPercent(r.accuracy) },
    { key: 'completion', header: 'Completion', align: 'right', hideOnMobile: true, sortValue: (r) => r.completion, cell: (r) => formatPercent(r.completion) },
    { key: 'attempt', header: 'Attempt', align: 'right', hideOnMobile: true, sortValue: (r) => r.attempt, cell: (r) => r.attempt },
    {
      key: 'status',
      header: 'Status',
      sortValue: (r) => r.status,
      cell: (r) => (
        <span className={`text-xs font-medium ${r.status === 'completed' ? 'text-foreground' : 'text-amber-500'}`}>
          {r.status === 'completed' ? 'Completed' : 'Partly answered'}
        </span>
      ),
    },
  ];

  if (error) return <AnalyticsErrorState message={error} />;

  const empty = !loading && results.length === 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-foreground">{self ? 'My performance' : 'Performance'}</h2>
        {!empty && <DateRangeFilter value={range} onChange={setRange} />}
      </div>

      {empty ? (
        <AnalyticsPanel title={self ? 'Your results will appear here' : 'No results yet'}>
          <AnalyticsEmptyState
            title="No performance data yet"
            description={self ? 'Finish a lesson quiz and your score, progress and feedback will show up here.' : 'Analytics will appear after this student completes a lesson quiz.'}
          />
        </AnalyticsPanel>
      ) : (
        <>
          <MetricGrid>
            <MetricCard
              loading={loading}
              label="Average score"
              value={formatPercent(summary.average)}
              detail={`${summary.validSubmissions} quiz${summary.validSubmissions === 1 ? '' : 'zes'}`}
              deltaPp={averageDelta}
              definition="Mean percentage across quizzes in this period."
            />
            <MetricCard loading={loading} label="Lessons completed" value={lessonsCompleted} detail="With a quiz answered" />
            <MetricCard
              loading={loading}
              label="Accuracy"
              value={formatPercent(summary.accuracy)}
              detail="Correct of attempted"
              definition="Correct answers ÷ questions attempted, averaged across quizzes."
            />
            <MetricCard
              loading={loading}
              label="Learning time"
              value={formatDuration(learningSeconds)}
              detail={launchSeconds > 0 ? 'Time in lessons' : learningSeconds > 0 ? 'Time on quizzes' : 'Not recorded'}
            />
            {best ? (
              <MetricCard loading={loading} label={self ? 'Strongest subject' : 'Best subject'} value={<span className="text-lg">{best.label}</span>} detail={`${formatPercent(best.summary.average)} average`} />
            ) : (
              <MetricCard loading={loading} label="Pass rate" value={formatPercent(summary.passRate)} detail={`At or above ${passThreshold}%`} />
            )}
            <MetricCard
              loading={loading}
              label="Recent trend"
              value={trend ? formatPp(trend.change) : '—'}
              detail={trend ? `Last ${trend.count} vs ${trend.count} before` : 'Needs 4+ quizzes'}
              definition="Average of the most recent quizzes compared with the same number just before them, in percentage points."
            />
          </MetricGrid>

          <div className="grid gap-5 lg:grid-cols-5">
            <AnalyticsPanel className="lg:col-span-3" title="Performance trend" description="Percentage on each quiz, oldest to newest">
              {loading ? (
                <ChartSkeleton />
              ) : trendData.length < 2 ? (
                <AnalyticsEmptyState compact title="Not enough quizzes yet" description="The trend appears after two quizzes in this period." />
              ) : (
                <TrendChart data={trendData} passThreshold={passThreshold} valueLabel="Score" />
              )}
            </AnalyticsPanel>
            <AnalyticsPanel
              className="lg:col-span-2"
              title={areaKey === 'subject' ? 'By subject' : 'By chapter'}
              description="Average percentage in each"
            >
              {loading ? (
                <ChartSkeleton />
              ) : areas.length < 2 ? (
                <AnalyticsEmptyState compact title="One area so far" description={`A comparison appears once there are quizzes in more than one ${areaKey}.`} />
              ) : (
                <ComparisonChart
                  data={areas.slice(0, 8).map((a) => ({ key: a.key, label: a.label, value: a.summary.average, note: `${a.summary.validSubmissions} quiz${a.summary.validSubmissions === 1 ? '' : 'zes'}` }))}
                  valueLabel="Average"
                />
              )}
            </AnalyticsPanel>
          </div>

          {(strengths.length > 0 || gaps.length > 0) && (
            <div className="grid gap-5 md:grid-cols-2">
              <AnalyticsPanel title={self ? 'Going well' : 'Strengths'} description={`${areaKey === 'subject' ? 'Subjects' : 'Chapters'} averaging 75% or more`}>
                {strengths.length === 0 ? (
                  <p className="text-sm text-muted-foreground">None yet.</p>
                ) : (
                  <AreaList areas={strengths} />
                )}
              </AnalyticsPanel>
              <AnalyticsPanel
                title={self ? 'Worth revisiting' : 'Needs attention'}
                description={`${areaKey === 'subject' ? 'Subjects' : 'Chapters'} averaging under 50%`}
              >
                {gaps.length === 0 ? <p className="text-sm text-muted-foreground">Nothing under 50%.</p> : <AreaList areas={gaps} />}
              </AnalyticsPanel>
            </div>
          )}

          <AnalyticsPanel title={self ? 'My results' : 'Lesson history'} description="Every quiz attempt. Open one to see each question.">
            {loading ? (
              <TableSkeleton />
            ) : (
              <DataTable
                rows={inPeriod}
                columns={columns}
                rowKey={(r) => r.id}
                initialSort={{ key: 'date', direction: 'desc' }}
                searchPlaceholder="Search lessons"
                searchText={(r) => `${lessonTitle(r)} ${r.subject ?? ''} ${r.chapterName ?? ''}`}
                pageSize={10}
                onRowClick={(r) => navigate(`/results/${r.id}`)}
                empty={<AnalyticsEmptyState compact title="No quizzes in this period" description="Try a longer date range." />}
              />
            )}
            <AnalyticsNote>
              Strengths and areas to revisit need at least two quizzes in a {areaKey}, so one result never labels a whole topic.
            </AnalyticsNote>
          </AnalyticsPanel>
        </>
      )}
    </div>
  );
}

function AreaList({ areas }: { areas: { key: string; label: string; average: number; submissions: number }[] }) {
  return (
    <ul className="divide-y divide-border">
      {areas.slice(0, 5).map((a) => (
        <li key={a.key} className="flex items-center justify-between gap-3 py-2 text-sm">
          <span className="min-w-0 truncate text-foreground">{a.label}</span>
          <span className="shrink-0 tabular-nums text-muted-foreground">
            <span className="font-semibold text-foreground">{formatPercent(a.average)}</span> · {a.submissions} quizzes
          </span>
        </li>
      ))}
    </ul>
  );
}
